import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Clear from "@mui/icons-material/Clear";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { SxProps, useTheme } from "@mui/material/styles";

import { clone, reverse } from "lodash";

import { useAppSelector } from "app/hooks";
import { neutral, primary } from "app/themes/colors";
import {
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import { useDebounce, useRecentlySearchedPatients } from "hooks/";
import { QUICK_LAUNCHER_FOCUS_PATIENT_SEARCH } from "hooks/use-event-bus/event-bus";
import { useEventBus } from "hooks/use-event-bus/use-event-bus.hook";
import { useQueryPatientsQuery } from "services/patients";
import type { Patient } from "services/types/patients.type";
import { getFullName } from "utils/common";

import AddNewPatientOption from "./add-new-patient-option.component";
import type { NewPatientSeed } from "./new-patient-seed.utils";
import {
  NEW_PATIENT_OPTION_ID,
  buildNewPatientSeed
} from "./new-patient-seed.utils";
import PatientSearchOption from "./patient-search-option.component";

export const PATIENTS_SEARCH_QUERY =
  "{id,sex,first_name,middle_name,preferred_name,last_name,birthdate,age_years,phone,phone_type,secondary_phone,email,secondary_email,email_bounced_at,email_bounce_detail,photo,is_active,primary_contact_notes,secondary_contact_notes,crm_segment,box_folder_id,city,state,preferred_clinic{id,name,region{id,name}},preferred_provider{full_name}}";

type PatientSearchProps = {
  onPatientSelected?: (patient?: GroupedPatient) => void;
  /**
   * Opt-in: when a search returns no patients, the dropdown offers an "Add a new
   * patient" row and hands back what we could read from the search text. The caller
   * owns the form/modal so this component stays free of the patient-form import graph.
   */
  onAddNewPatient?: (seed: NewPatientSeed) => void;
  placeholder?: string;
  minWidth?: number;
  disableClearable?: boolean;
  showRecent?: boolean;
  showLengthLimitHint?: boolean;
  showSearchModeToggle?: boolean;
  clinicId?: number;
  /**
   * Opt-in: answer the quick launcher's "focus the search" event. Exactly one
   * mounted instance should set this — the app shell's top bar — since several
   * others (leads, dialer, per-page pickers) render the same component and would
   * otherwise all steal focus at once.
   */
  respondToGlobalFocusRequest?: boolean;
  sx?: SxProps;
};

type GroupKey = "recent" | "search";
type PatientSearchMode = "patients" | "serial";

export interface GroupedPatient extends Patient {
  group_key: GroupKey;
}

export default function PatientSearch({
  onPatientSelected,
  onAddNewPatient,
  minWidth = 375,
  showLengthLimitHint,
  placeholder = "Search patients by name, phone number, email, id",
  disableClearable = false,
  showRecent = false,
  showSearchModeToggle = false,
  clinicId,
  respondToGlobalFocusRequest = false,
  sx
}: PatientSearchProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const showInactivePatients = Boolean(
    currentPractice?.show_deactivated_patients_in_search
  );
  const effectiveClinicId = currentClinic?.id ?? clinicId;
  const [search, setSearch] = useState<string>("");
  const [searchMode, setSearchMode] = useState<PatientSearchMode>("patients");
  const isSerialSearch = showSearchModeToggle && searchMode === "serial";
  const [selectedPatient, setSelectedPatient] = useState<GroupedPatient | null>(
    null
  );
  const [open, setOpen] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);
  const { value: dSearch, isDebouncing } = useDebounce(search, 200);
  const { addPatientToRecent, recentPatientIds } =
    useRecentlySearchedPatients();
  const [patients, setPatients] = useState<GroupedPatient[]>([]);
  const {
    data: searchPatients,
    // `data` keeps the previous search's response while a new one is in flight, so the
    // "nothing matched" decision has to read `currentData` — the response for these
    // exact args, or undefined while skipped/fetching.
    currentData: currentSearchPatients,
    isFetching: isFetchingForSearch,
    isLoading: isLoadingSearch,
    isUninitialized,
    refetch
  } = useQueryPatientsQuery(
    {
      search: isSerialSearch ? undefined : dSearch,
      patient_product_serial_number: isSerialSearch ? dSearch : undefined,
      baseFilter: {
        preferred_clinic: Boolean(currentPractice?.limit_to_current_clinic)
          ? effectiveClinicId
          : undefined
      },
      is_active: showInactivePatients ? undefined : true,
      query: PATIENTS_SEARCH_QUERY
    },
    {
      skip:
        !open ||
        !currentPractice ||
        !effectiveClinicId ||
        !dSearch ||
        dSearch.length < 3,
      refetchOnMountOrArgChange: true
    }
  );

  const { data: recentPatients, isFetching: isFetchingForRecent } =
    useQueryPatientsQuery(
      {
        search: dSearch.length < 3 ? "" : dSearch,
        ids: recentPatientIds,
        baseFilter: {
          preferred_clinic: Boolean(currentPractice?.limit_to_current_clinic)
            ? currentClinic?.id
            : undefined
        },
        is_active: showInactivePatients ? undefined : true,
        query: PATIENTS_SEARCH_QUERY
      },
      {
        skip:
          !open ||
          !currentPractice ||
          !effectiveClinicId ||
          !showRecent ||
          isSerialSearch ||
          recentPatientIds?.length === 0
      }
    );
  const [isFetching, setIsFetching] = useState(
    isFetchingForSearch || isFetchingForRecent
  );

  useEffect(() => {
    setIsFetching(isFetchingForSearch || isFetchingForRecent);
  }, [isFetchingForSearch, isFetchingForRecent]);

  useEffect(() => {
    if (
      open &&
      dSearch.length >= 3 &&
      !isFetchingForSearch &&
      !isLoadingSearch
    ) {
      setPatients([
        ...(searchPatients
          ? searchPatients.map((patient) => ({
              ...patient,
              group_key: "search" as GroupKey
            }))
          : [])
      ]);
    } else if (isSerialSearch && open) {
      setPatients([]);
    } else if (!isSerialSearch && open && (showRecent || patients.length > 0)) {
      const reversedRecentPatientIds = reverse(clone(recentPatientIds));
      setPatients(
        reversedRecentPatientIds
          .map((id: number) => {
            const patient = recentPatients?.find(
              (patient) => patient.id === id
            );
            return patient
              ? {
                  ...patient,
                  group_key: "recent"
                }
              : undefined;
          })
          .filter(Boolean)
      );
    }
  }, [
    searchPatients,
    recentPatientIds,
    recentPatients,
    search,
    patients.length,
    showRecent,
    isFetchingForSearch,
    isLoadingSearch,
    open,
    dSearch.length,
    isSerialSearch
  ]);

  useEffect(() => {
    if (showRecent && selectedPatient) {
      addPatientToRecent(selectedPatient.id);
    }
  }, [search, selectedPatient, addPatientToRecent, focus, showRecent]);

  const showRecentItems =
    !isSerialSearch &&
    showRecent &&
    (dSearch.length < 3 || isFetchingForSearch || isLoadingSearch);

  // Only once the response for what is currently typed has landed and is empty — never
  // while debouncing or fetching, which would flash the row mid-typing.
  const showAddNewPatient =
    Boolean(onAddNewPatient) &&
    open &&
    !isSerialSearch &&
    !isDebouncing &&
    !isFetchingForSearch &&
    !isLoadingSearch &&
    dSearch.length >= 3 &&
    currentSearchPatients?.length === 0;

  const newPatientSeed = useMemo(() => buildNewPatientSeed(search), [search]);

  const options: GroupedPatient[] = showAddNewPatient
    ? [
        // Synthetic row; only `id` is read by the option renderer and the change handler.
        { id: NEW_PATIENT_OPTION_ID, group_key: "search" } as GroupedPatient
      ]
    : patients;

  const handleSearchModeChange = (
    _: unknown,
    value: PatientSearchMode | null
  ) => {
    if (!value) {
      return;
    }
    setSearchMode(value);
    setSearch("");
    setSelectedPatient(null);
    setPatients([]);
    setOpen(false);
    setFocus(false);
    if (onPatientSelected) {
      onPatientSelected(undefined);
    }
  };

  // Focus goes through the container rather than a ref on the input: MUI's
  // Autocomplete owns the input's ref via `params.inputProps`, and handing
  // InputBase a competing `inputRef` detaches the listbox's keyboard handling.
  // `onFocus` above already opens the dropdown, so a plain `.focus()` is enough.
  useEventBus(QUICK_LAUNCHER_FOCUS_PATIENT_SEARCH, () => {
    if (!respondToGlobalFocusRequest) return;
    containerRef.current?.querySelector("input")?.focus();
  });

  return (
    <Box
      ref={containerRef}
      sx={{
        boxSizing: "border-box",
        boxShadow: theme.shadows[0],
        borderRadius: "28px",
        pl: showSearchModeToggle ? 0.5 : 1,
        display: "flex",
        alignItems: "center",
        minWidth: theme.typography.pxToRem(minWidth),
        width: "100%",
        border: `solid 1px ${showSearchModeToggle ? neutral[300] : primary[500]}`,
        ...sx
      }}
    >
      {showSearchModeToggle && (
        <>
          <ToggleButtonGroup
            exclusive
            value={searchMode}
            onChange={handleSearchModeChange}
            size="small"
            aria-label="patient-search-mode"
            sx={{
              backgroundColor: neutral[100],
              borderRadius: "24px",
              flexShrink: 0,
              p: 0.25,
              "& .MuiToggleButtonGroup-grouped": {
                border: 0,
                borderRadius: "20px !important",
                color: neutral[700],
                fontWeight: 600,
                height: "32px",
                px: 2,
                textTransform: "none",
                "&.Mui-selected": {
                  backgroundColor: primary[500],
                  color: theme.palette.common.white,
                  "&:hover": {
                    backgroundColor: primary[500]
                  }
                },
                "&:hover": {
                  backgroundColor: "transparent"
                }
              }
            }}
          >
            <ToggleButton value="patients">Patients</ToggleButton>
            <ToggleButton value="serial">Serial #</ToggleButton>
          </ToggleButtonGroup>
          <Box
            sx={{
              borderLeft: `1px solid ${neutral[200]}`,
              flexShrink: 0,
              height: "32px",
              ml: 1.25,
              mr: 1
            }}
          />
        </>
      )}
      <Autocomplete
        data-testid="top-nav-search"
        openOnFocus
        // Never auto-highlight the add row: Enter mid-typing would open the form.
        autoHighlight={!showRecentItems && !showAddNewPatient}
        open={open}
        autoComplete={false}
        clearOnBlur={false}
        blurOnSelect
        fullWidth
        popupIcon={<ExpandMoreOutlinedIcon />}
        options={options}
        filterOptions={(options) => options}
        disableClearable={disableClearable}
        forcePopupIcon={false}
        loading={isFetching || isDebouncing}
        inputValue={search}
        value={selectedPatient}
        onFocus={() => {
          if (!isUninitialized) {
            refetch();
          }
          setTimeout(() => {
            setPatients([]);
            setOpen(true);
            setFocus(true);
          }, 1);
        }}
        onBlur={() => {
          setPatients([]);
          setOpen(false);
          setFocus(false);
        }}
        noOptionsText="No results"
        onInputChange={(_, value, reason) => {
          if (reason === "reset") {
            setSearch("");
            setSelectedPatient(null);
          } else {
            setSearch(value);
          }
        }}
        onChange={(_, value) => {
          if (value?.id === NEW_PATIENT_OPTION_ID) {
            // MUI has already reset the input by now; put the text back so the search
            // survives cancelling out of the form.
            setSearch(search);
            setSelectedPatient(null);
            setOpen(false);
            setFocus(false);
            onAddNewPatient?.(newPatientSeed);
            return;
          }
          setSelectedPatient(value || null);
          if (onPatientSelected) {
            onPatientSelected(value || undefined);
          } else if (value) {
            navigate(`patients/${value.id}/`);
          }
          setOpen(false);
          setFocus(false);
        }}
        getOptionLabel={(option: GroupedPatient) =>
          option?.id === NEW_PATIENT_OPTION_ID
            ? ""
            : getFullName({
                firstName: option?.first_name,
                middleName: option?.middle_name,
                preferredName: option?.preferred_name,
                lastName: option?.last_name
              })
        }
        renderInput={({ InputLabelProps, InputProps, ...params }) => (
          <InputBase
            {...InputProps}
            {...params}
            sx={{ pr: 0 }}
            placeholder={
              isSerialSearch ? "Search by product serial number" : placeholder
            }
            startAdornment={<SearchIcon sx={{ mr: 0.25 }} color="action" />}
            endAdornment={
              <Stack
                direction="row"
                columnGap={0.5}
                sx={{ height: "40px", alignItems: "center" }}
              >
                {showLengthLimitHint &&
                  open &&
                  search.length > 0 &&
                  search.length < 3 && (
                    <Typography sx={{ color: neutral[600], mr: 1 }}>
                      Please enter at least 3 characters
                    </Typography>
                  )}

                {(isFetching || isDebouncing) && dSearch.length >= 3 && (
                  <CircularProgress
                    color="primary"
                    size="small"
                    sx={{
                      width: "25px",
                      height: "25px"
                    }}
                  />
                )}
                <IconButton
                  sx={{
                    visibility: search.length > 0 ? "visible" : "hidden",
                    mr: 0.25
                  }}
                  onClick={() => {
                    setSearch("");
                    setSelectedPatient(null);
                    setOpen(false);
                    setFocus(false);
                    if (onPatientSelected) {
                      onPatientSelected(undefined);
                    }
                  }}
                  size="small"
                  edge="end"
                >
                  <Clear color="primary" />
                </IconButton>
              </Stack>
            }
          />
        )}
        slotProps={{
          popper: {
            ...(showSearchModeToggle && {
              anchorEl: containerRef.current,
              style: { width: containerRef.current?.clientWidth }
            }),
            sx: {
              zIndex: (theme) => theme.zIndex.snackbar + 1
            }
          }
        }}
        groupBy={
          // Passing groupBy unconditionally would put an empty group header above every
          // real result, so it is only supplied for the two grouped states.
          showAddNewPatient
            ? () => `No patients matched "${search.trim()}"`
            : showRecentItems
              ? (option: GroupedPatient) =>
                  option.group_key === "recent" ? "Recently viewed" : ""
              : undefined
        }
        renderOption={(props, option: GroupedPatient) =>
          option.id === NEW_PATIENT_OPTION_ID ? (
            <AddNewPatientOption
              renderProps={props}
              seed={newPatientSeed}
              key={option.id}
            />
          ) : (
            <PatientSearchOption
              renderProps={props}
              option={option}
              key={option.id}
              showPatientTags={!isDebouncing && !isFetchingForSearch}
            />
          )
        }
      />
    </Box>
  );
}
